<template>
  <q-overlay
    v-model="open"
    :opacity="0"
    cursor-type="default"
  >
    <template #body>
      <q-drawer
        key="filters"
        v-model="open"
        class="dashboard-filters"
        :width="$q.screen.lt.sm ? $q.screen.width : 400"
        behavior="mobile"
        side="right"
        overlay
        bordered
        elevated
      >
        <q-card class="column no-wrap fit">
          <q-card-section class="column q-pa-none justify-between">
            <div class="row justify-between bg-grey-2">
              <slot name="title">
                <div class="row re-ma">
                  <re-icon
                    name="mdi-filter-variant"
                    class="q-mr-sm"
                    size="xs"
                  />
                  <h5 class="col-auto">
                    {{ $t('dashboard.filters.title') }}
                  </h5>
                </div>
              </slot>
              <q-btn
                icon="mdi-close"
                size="xs"
                flat
                round
                outline
                class="text-black bg-transparent self-center q-mr-lg bg-grey-4"
                @click="open = false"
              />
            </div>
            <q-separator />
          </q-card-section>
          <q-card-section class="main-content fit">
            <q-scroll-area class="fit">
              <div class="q-gutter-md">
                <template
                  v-for="col in columns"
                  :key="col.name"
                >
                  <div v-if="col.searchable || col.headerComponent || col.selectOptions">
                    <column-filter
                      :col="col"
                      :model-value="localFilters"
                    />
                  </div>
                </template>
              </div>
            </q-scroll-area>
          </q-card-section>
          <q-separator />
          <q-card-section>
            <div
              :class="[$q.screen.xs ? 'column reverse' : 'row']"
              class="q-gutter-x-md q-gutter-y-sm justify-end xs-btn-full-width"
            >
              <re-button
                v-close-popup
                size="md"
                :label="$t('dashboard.filters.clear')"
                :readonly="false"
                :disabled="!hasFilters"
                @click="clearFilters"
              />
              <re-button
                v-close-popup
                color="main"
                :readonly="false"
                size="md"
                :label="$t('dashboard.filters.apply')"
                @click="applyFilters"
              />
            </div>
          </q-card-section>
        </q-card>
      </q-drawer>
    </template>
  </q-overlay>
</template>

<script setup>
import { QOverlay } from '@quasar/quasar-ui-qoverlay'
import { cloneDeep } from 'lodash-es'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import ColumnFilter from './core/slots/ColumnFilter.vue'

const { t, te } = useI18n()

const open = defineModel('open', {
  required: false,
  default: false,
  type: Boolean,
})

const colFilters = defineModel('colFilters', {
  type: Object,
  required: true,
})

const props = defineProps({
  config: {
    type: Object,
    required: true,
  },
})

const localFilters = ref({})

watch(open, (val) => {
  if (val) {
    localFilters.value = cloneDeep(colFilters.value || {})
  }
})

const hasFilters = computed(() => {
  return Object.keys(colFilters.value).length > 0
})

const columns = computed(() => {
  return props.config.columns.value
    .map((column) => ({
      field: column.field || column.name,
      sortable: column.sortable !== false,
      visible: column.visible !== false,
      showFilter: column.showFilter !== false,
      align: column.align || 'left',
      sort: column.sort,
      ...column,
      label: te(column.label) ? t(column.label) : column.label || '',
    }))
    .filter((col) => col.visible || col.showFilter)
})

const applyFilters = () => {
  Object.keys(colFilters.value).forEach((k) => delete colFilters.value[k])
  Object.entries(localFilters.value).forEach(([k, v]) => {
    colFilters.value[k] = v
  })
  open.value = false
}

const clearFilters = () => {
  localFilters.value = {}
  Object.keys(colFilters.value).forEach((k) => delete colFilters.value[k])
  open.value = false
}
</script>
